import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../interceptors";

const TTL_MS = 60_000;

// Single freshness check so the thunks short-circuit identically.
const isFresh = (fetchedAt, list) =>
  Array.isArray(list) && list.length > 0 && Date.now() - fetchedAt < TTL_MS;

export const fetchPublishedJobsCached = createAsyncThunk(
  "jobsCache/fetchPublished",
  async (_arg, { getState, rejectWithValue }) => {
    const { publishedJobs, publishedFetchedAt } =
      getState().jobsCacheSliceReducer || {};
    if (isFresh(publishedFetchedAt, publishedJobs)) {
      return { cached: true, list: publishedJobs };
    }
    try {
      const userId = localStorage.getItem("userId") || "1";
      const r = await axiosInstance.post(
        "/job-posting/job-details/get-all-published-jobs",
        { userId, start: "0", length: "100" }
      );
      return { cached: false, list: r.data?.list || [] };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load published jobs");
    }
  }
);

export const fetchAllJobsCached = createAsyncThunk(
  "jobsCache/fetchAll",
  async (_arg, { getState, rejectWithValue }) => {
    const { allJobs, allFetchedAt } = getState().jobsCacheSliceReducer || {};
    if (isFresh(allFetchedAt, allJobs)) {
      return { cached: true, list: allJobs };
    }
    try {
      const userId = localStorage.getItem("userId") || "1";
      const r = await axiosInstance.post(
        "/job-posting/job-details/get-all-jobs",
        { userId, start: "0", length: "100" }
      );
      return { cached: false, list: r.data?.list || [] };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load all jobs");
    }
  }
);

const jobsCacheSlice = createSlice({
  name: "jobsCache",
  initialState: {
    publishedJobs: [],
    allJobs: [],
    publishedFetchedAt: 0,
    allFetchedAt: 0,
    publishedLoading: false,
    allLoading: false,
    publishedError: null,
    allError: null,
  },
  reducers: {
    setPublishedJobs(state, { payload }) {
      state.publishedJobs = payload || [];
      state.publishedFetchedAt = Date.now();
    },
    setAllJobs(state, { payload }) {
      state.allJobs = payload || [];
      state.allFetchedAt = Date.now();
    },
    clearPublishedJobs(state) {
      state.publishedJobs = [];
      state.publishedFetchedAt = 0;
    },
    clearAllJobs(state) {
      state.allJobs = [];
      state.allFetchedAt = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublishedJobsCached.pending, (state) => {
        state.publishedLoading = true;
        state.publishedError = null;
      })
      .addCase(fetchPublishedJobsCached.fulfilled, (state, { payload }) => {
        state.publishedLoading = false;
        if (!payload?.cached) {
          state.publishedJobs = payload?.list || [];
          state.publishedFetchedAt = Date.now();
        }
      })
      .addCase(fetchPublishedJobsCached.rejected, (state, { payload }) => {
        state.publishedLoading = false;
        state.publishedError = payload || "Failed to load published jobs";
      })
      .addCase(fetchAllJobsCached.pending, (state) => {
        state.allLoading = true;
        state.allError = null;
      })
      .addCase(fetchAllJobsCached.fulfilled, (state, { payload }) => {
        state.allLoading = false;
        if (!payload?.cached) {
          state.allJobs = payload?.list || [];
          state.allFetchedAt = Date.now();
        }
      })
      .addCase(fetchAllJobsCached.rejected, (state, { payload }) => {
        state.allLoading = false;
        state.allError = payload || "Failed to load all jobs";
      });
  },
});

export const {
  setPublishedJobs,
  setAllJobs,
  clearPublishedJobs,
  clearAllJobs,
} = jobsCacheSlice.actions;

export const selectPublishedJobs = (s) =>
  s.jobsCacheSliceReducer?.publishedJobs || [];
export const selectAllJobs = (s) => s.jobsCacheSliceReducer?.allJobs || [];
export const selectPublishedJobsLoading = (s) =>
  !!s.jobsCacheSliceReducer?.publishedLoading;
export const selectAllJobsLoading = (s) =>
  !!s.jobsCacheSliceReducer?.allLoading;

export default jobsCacheSlice.reducer;
